export const FEATURE_NAME = "vehicle-tracker";

/** Forensic body codes emitted by the traffic-classification-core classify step. */
export enum VehicleType {
  unknown = "unknown",
  TIP = "TIP",
  SKP = "SKP",
  UTE = "UTE",
  VAN = "VAN",
  SUV = "SUV",
  HATCH = "HATCH",
  SED = "SED",
  REF = "REF",
  BUS = "BUS",
  MIX = "MIX",
  SEMI = "SEMI",
  TNK = "TNK",
  FLT = "FLT",
  CRN = "CRN",
  MOTO = "MOTO",
  TRK = "TRK",
}

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  [VehicleType.unknown]: "unknown",
  [VehicleType.TIP]: "tipper",
  [VehicleType.SKP]: "skip truck",
  [VehicleType.UTE]: "ute",
  [VehicleType.VAN]: "van",
  [VehicleType.SUV]: "SUV",
  [VehicleType.HATCH]: "hatchback",
  [VehicleType.SED]: "sedan",
  [VehicleType.REF]: "refuse truck",
  [VehicleType.BUS]: "bus",
  [VehicleType.MIX]: "cement mixer",
  [VehicleType.SEMI]: "semi-trailer",
  [VehicleType.TNK]: "tanker",
  [VehicleType.FLT]: "flatbed",
  [VehicleType.CRN]: "mobile crane",
  [VehicleType.MOTO]: "motorcycle",
  [VehicleType.TRK]: "truck",
};

export const VEHICLE_TYPE_VALUES: VehicleType[] = [
  VehicleType.UTE,
  VehicleType.TRK,
  VehicleType.FLT,
  VehicleType.TIP,
  VehicleType.SKP,
  VehicleType.REF,
  VehicleType.VAN,
  VehicleType.TNK,
  VehicleType.CRN,
  VehicleType.MIX,
  VehicleType.SEMI,
  VehicleType.BUS,
  VehicleType.SUV,
  VehicleType.HATCH,
  VehicleType.SED,
  VehicleType.MOTO,
  VehicleType.unknown,
];

export const VEHICLE_TYPE_GROUP: Record<VehicleType, string> = {
  [VehicleType.UTE]: "Commercial & heavy vehicles",
  [VehicleType.TRK]: "Commercial & heavy vehicles",
  [VehicleType.TIP]: "Commercial & heavy vehicles",
  [VehicleType.SKP]: "Commercial & heavy vehicles",
  [VehicleType.REF]: "Commercial & heavy vehicles",
  [VehicleType.VAN]: "Commercial & heavy vehicles",
  [VehicleType.SEMI]: "Commercial & heavy vehicles",
  [VehicleType.TNK]: "Commercial & heavy vehicles",
  [VehicleType.FLT]: "Commercial & heavy vehicles",
  [VehicleType.CRN]: "Commercial & heavy vehicles",
  [VehicleType.MIX]: "Commercial & heavy vehicles",
  [VehicleType.BUS]: "Commercial & heavy vehicles",
  [VehicleType.SUV]: "Passenger vehicles",
  [VehicleType.HATCH]: "Passenger vehicles",
  [VehicleType.SED]: "Passenger vehicles",
  [VehicleType.MOTO]: "Passenger vehicles",
  [VehicleType.unknown]: "Passenger vehicles",
};

/** Broad vehicle class: light (cars, utes, vans…) or heavy (trucks, buses…). */
export type VehicleClass = "light" | "heavy";

/**
 * Light or heavy class per body type, shown in the vehicle log and the
 * export's Classification column (DEL-1156). `unknown` has no class. Proposed
 * mapping, pending product's answer on open question 1 of the vehicle tracker
 * export plan; the vehicle log design counts buses as light, which product has
 * not confirmed (DEL-1170), so buses stay heavy here.
 */
export const VEHICLE_CLASS: Record<VehicleType, VehicleClass | null> = {
  [VehicleType.UTE]: "light",
  [VehicleType.VAN]: "light",
  [VehicleType.SUV]: "light",
  [VehicleType.HATCH]: "light",
  [VehicleType.SED]: "light",
  [VehicleType.MOTO]: "light",
  [VehicleType.TRK]: "heavy",
  [VehicleType.TIP]: "heavy",
  [VehicleType.SKP]: "heavy",
  [VehicleType.REF]: "heavy",
  [VehicleType.SEMI]: "heavy",
  [VehicleType.TNK]: "heavy",
  [VehicleType.FLT]: "heavy",
  [VehicleType.CRN]: "heavy",
  [VehicleType.MIX]: "heavy",
  [VehicleType.BUS]: "heavy",
  [VehicleType.unknown]: null,
};

/** The export's Classification column text per class. */
export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  light: "Light vehicle",
  heavy: "Heavy vehicle",
};

export const LOAD_STATUSES = ["empty", "partial", "full", "overloaded", "covered", "unknown"] as const;

export type LoadStatus = (typeof LOAD_STATUSES)[number];

/** Direction of travel as the camera reports it, relative to the direction set on the camera. */
export const TRAVEL_DIRECTIONS = ["in", "out", "unknown"] as const;

export type TravelDirection = (typeof TRAVEL_DIRECTIONS)[number];

export const TRAVEL_DIRECTION_LABELS: Record<TravelDirection, string> = {
  in: "Entry",
  out: "Exit",
  unknown: "Unknown",
};

export const ATTRIBUTE_KEYS = ["sign_text", "tray", "trailer"] as const;

export type AttributeKey = (typeof ATTRIBUTE_KEYS)[number];

export const ATTRIBUTE_LABELS: Partial<Record<AttributeKey, string>> = {
  sign_text: "Has signage",
  tray: "Has tray",
  trailer: "Towing trailer",
};

export function getAttributeLabel(key: string): string {
  return (
    ATTRIBUTE_LABELS[key as AttributeKey] ?? key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, " ")
  );
}

/** Attribute keys whose filter value is a bare `true` — the only ones a boolean toggle can drive. */
export const BOOLEAN_ATTRIBUTE_KEYS = ["trailer"] as const;

export type BooleanAttributeKey = (typeof BOOLEAN_ATTRIBUTE_KEYS)[number];

/**
 * Tray codes VT's classifier emits. VT never documents these beyond the bare enum in its prompt, so
 * the labels are reconstructed here:
 *
 * - `ST_FLAT`, `STYLE`, `DROP` are confirmed against VT's benchmark ground truth
 *   (`apps/benchmark/dataset/cases.json`): `STYLE` is exclusively factory-tub dual-cab utes,
 *   `ST_FLAT` is cab-chassis utes and light trucks, `DROP` is light cab-chassis trucks.
 * - `WD_FLAT`, `HOOK`, `SWING` have no ground-truth cases and are inferred: `WD_FLAT` by symmetry
 *   with `ST_FLAT` (wood vs. steel deck), `HOOK`/`SWING` from VT's own SKP body-code description
 *   ("loaded/unloaded via hook or chain lift").
 *
 * `tray` itself is not restricted to this set — VT's prompt allows `<tt_code|string|null>` — so
 * this is only the option list for the picker, not a validation constraint.
 */
export const TRAY_TYPES = ["ST_FLAT", "WD_FLAT", "STYLE", "DROP", "HOOK", "SWING"] as const;

export type TrayType = (typeof TRAY_TYPES)[number];

export const TRAY_TYPE_LABELS: Record<TrayType, string> = {
  ST_FLAT: "Steel flat tray",
  WD_FLAT: "Timber flat tray",
  STYLE: "Styleside tub",
  DROP: "Drop-side tray",
  HOOK: "Hook lift",
  SWING: "Swing lift",
};

/**
 * Label for a tray code. Falls back to the raw code for a value outside `TRAY_TYPES` — VT's
 * `tray` flag is not a closed enum, so a stored row (or a hand-built filter URL) can carry
 * free text the picker never offers.
 */
export function getTrayTypeLabel(code: string): string {
  return (TRAY_TYPE_LABELS as Record<string, string | undefined>)[code] ?? code;
}

/**
 * a3s filter key -> the camelCase key VT's `/v3/vehicles` `vehicleFeatures` filter recognises.
 *
 * Mirrors `VEHICLE_FEATURE_KEYS` in the vehicle-tracker repo
 * (`packages/traffic-classification-core/src/vehicle-feature-keys.ts`), which is the source of
 * truth — VT builds its filter parsing from it. The two are maintained by hand in separate repos.
 *
 * Drift here is silent: since DEL-391 VT *drops* a key it does not recognise rather than
 * returning 400, so a stale entry yields a wider, unfiltered result set, never an error. If you
 * change this map, change VT's in the same breath.
 */
export const ATTRIBUTE_KEY_TO_VT_FEATURE = {
  sign_text: "signText",
  tray: "tray",
  trailer: "trailer",
} as const satisfies Record<AttributeKey, string>;
